import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../../../hooks/useToast';

interface UserFormData {
  email: string;
  password: string;
  name: string;
  organization: string;
  phoneNumber: string;
  userLevel: number;
}

interface UserAddFormProps {
  currentUserLevel?: number | null;
}

const UserAddForm: React.FC<UserAddFormProps> = ({ currentUserLevel }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    name: '',
    organization: '',
    phoneNumber: '',
    userLevel: 9
  });

  // 유틸리티 함수들
  const getUserLevelText = useCallback((level: number): string => {
    switch (level) {
      case 1: return '최고 관리자';
      case 2: return '일반 관리자';
      case 3: return '공사 관계자';
      case 4: return '센터 관계자';
      case 5: return '입주업체';
      case 6: return '입주협력업체';
      case 7: return '공공기관';
      case 8: return '항만근로자';
      case 9: return '일반 사용자';
      default: return '알 수 없음';
    }
  }, []);

  // 권한별 선택 가능한 사용자 레벨 옵션
  const availableUserLevels = useMemo(() => {
    const levels = [
      { value: 9, label: '일반 사용자' },
      { value: 8, label: '항만근로자' },
      { value: 7, label: '공공기관' },
      { value: 6, label: '입주협력업체' },
      { value: 5, label: '입주업체' },
      { value: 4, label: '센터 관계자' },
      { value: 3, label: '공사 관계자' }
    ];

    // 최고관리자만 일반관리자 계정을 생성할 수 있음
    if (currentUserLevel === 1) {
      levels.push({ value: 2, label: '일반 관리자' });
    }

    return levels.sort((a, b) => b.value - a.value); // 높은 권한부터 정렬
  }, [currentUserLevel]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // 전화번호는 숫자만 입력 가능
    if (name === 'phoneNumber') {
      const cleaned = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: cleaned
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'userLevel' ? parseInt(value) : value
    }));
  }, []);

  // 강화된 폼 검증
  const validateForm = useCallback((): boolean => {
    // 필수 필드 검증
    if (!formData.name.trim()) {
      showToast('이름을 입력해주세요.', { type: 'warning' });
      return false;
    }

    if (!formData.email.trim()) {
      showToast('이메일을 입력해주세요.', { type: 'warning' });
      return false;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('올바른 이메일 형식을 입력해주세요.', { type: 'warning' });
      return false;
    }

    // 비밀번호 강도 검증
    if (formData.password.length < 8) {
      showToast('비밀번호는 최소 8자 이상이어야 합니다.', { type: 'warning' });
      return false;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(formData.password)) {
      showToast('비밀번호는 대문자, 소문자, 숫자, 특수문자를 각각 1개 이상 포함해야 합니다.', { type: 'warning' });
      return false;
    }

    if (!formData.organization.trim()) {
      showToast('소속을 입력해주세요.', { type: 'warning' });
      return false;
    }

    // 전화번호 형식 검증
    const phoneRegex = /^01[0-9]{8,9}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      showToast('전화번호는 하이픈(-) 없이 숫자만 입력해주세요. (예: 01012345678)', { type: 'warning' });
      return false;
    }

    // 권한 레벨 검증
    if (formData.userLevel === 2 && currentUserLevel !== 1) {
      showToast('일반 관리자 계정은 최고관리자만 생성할 수 있습니다.', { type: 'warning' });
      return false;
    }

    if (formData.userLevel === 1) {
      showToast('최고관리자 계정은 생성할 수 없습니다.', { type: 'warning' });
      return false;
    }

    return true;
  }, [formData, currentUserLevel, showToast]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 폼 검증
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const createUser = httpsCallable<UserFormData, { uid: string }>(functions, 'createUser');
      const result = await createUser(formData);

      showToast(`새로운 사용자 '${formData.name}'이 성공적으로 추가되었습니다.`, { type: 'success' });

      // 약간의 딜레이 후 페이지 이동 (Toast 메시지를 확인할 시간 제공)
      setTimeout(() => {
        navigate('/admin/users');
      }, 1500);
    } catch (err) {
      console.error('사용자 생성 중 오류:', err);
      let errorMessage = '사용자 생성 중 오류가 발생했습니다.';

      // Firebase Functions 에러 처리
      if (err instanceof Error) {
        if (err.message.includes('email-already-exists')) {
          errorMessage = '이미 존재하는 이메일 주소입니다.';
        } else if (err.message.includes('weak-password')) {
          errorMessage = '비밀번호가 너무 약합니다. 더 강한 비밀번호를 사용해주세요.';
        } else if (err.message.includes('invalid-email')) {
          errorMessage = '유효하지 않은 이메일 주소입니다.';
        } else {
          errorMessage = err.message;
        }
      }

      showToast(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, navigate, showToast]);

  return (
    <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
      <div className="p-8">
        <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
          사용자 정보
        </h2>
        <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
        

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
                이메일 주소
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              />
            </div>

            <div>
              <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
                비밀번호
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="최소 8자, 대소문자, 숫자, 특수문자 포함"
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              />
              <p className="mt-2 text-[#6b7684] text-sm font-normal font-['Pretendard']">
                대문자, 소문자, 숫자, 특수문자(@$!%*?&)를 각각 1개 이상 포함
              </p>
            </div>

            <div>
              <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
                이름
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              />
            </div>

            <div>
              <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
                소속
              </label>
              <input
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                required
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              />
            </div>

            <div>
              <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
                전화번호
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
                placeholder="01012345678"
                maxLength={11}
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              />
              <p className="mt-2 text-[#6b7684] text-sm font-normal font-['Pretendard']">하이픈(-) 없이 숫자만 입력: 01012345678</p>
            </div>

            <div>
              <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
                사용자 권한
              </label>
              <select
                name="userLevel"
                value={formData.userLevel}
                onChange={handleChange}
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              >
                {availableUserLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              {currentUserLevel !== 1 && (
                <p className="mt-2 text-[#6b7684] text-sm font-normal font-['Pretendard']">
                  * 일반 관리자 계정 생성은 최고관리자만 가능합니다.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center pt-6">
            <div className="text-sm text-[#6b7684]">
              <span className="text-blue-600">* 모든 필수 정보를 정확히 입력해주세요.</span>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 border border-gray-300 text-[#6b7684] text-base font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    처리중...
                  </div>
                ) : (
                  '사용자 추가'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserAddForm; 